import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, Scale } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "../i18n/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { useConfig, symbolForCurrency, nf, formatMoney } from "../lib/format";
import { Modal } from "./Modal";

const CAT_LABEL = { decoration: "plan.decoration", ceia: "plan.ceia", table: "budget.table", gifts: "nav.gifts", others: "budget.others" };

// Budget guard: run before confirming an add. If the amount exceeds the category
// and/or total budget, shows an elegant alert (Back / Adjust / Continue). Never blocks.
// When a category is exceeded and another category has spare budget, it also SUGGESTS
// rebalancing (transfer) right inside the modal — always with confirmation.
export function useBudgetGuard() {
  const { t } = useI18n();
  const { user } = useAuth();
  const config = useConfig();
  const navigate = useNavigate();
  const [state, setState] = useState(null); // {preview, category, amount, onProceed, donor, shortfall}
  const symbol = symbolForCurrency(config, user?.currency || "BRL");
  const fmt = (v) => formatMoney(symbol, v || 0);

  const guard = async (category, amount, onProceed) => {
    try {
      const { data } = await api.post("/budget/check", { category, amount });
      if (data.category.exceeds || data.total.exceeds) {
        let donor = null;
        const shortfall = data.category.over_after || 0;
        if (data.category.exceeds && shortfall > 0) {
          try {
            const { data: bd } = await api.get("/budget");
            const cands = Object.entries(bd.budget.categories)
              .filter(([k, v]) => k !== category && v.limit != null && (v.available || 0) >= shortfall)
              .sort((a, b) => (b[1].available || 0) - (a[1].available || 0));
            if (cands.length) donor = { key: cands[0][0], available: cands[0][1].available };
          } catch { /* ignore */ }
        }
        setState({ preview: data, category, amount, onProceed, donor, shortfall });
      } else onProceed();
    } catch { onProceed(); }
  };

  const cont = async () => {
    const s = state; setState(null);
    await s.onProceed();  // persist first so the overage reflects the new item
    api.post("/budget/commit", { category: s.category, amount: s.amount }).catch(() => {});
  };
  const back = async () => {
    const s = state; setState(null);
    await api.post("/budget/warning-cancelled", { category: s.category, amount: s.amount }).catch(() => {});
  };
  const adjust = () => { setState(null); navigate("/app/budget"); };
  const rebalanceAndContinue = async () => {
    const s = state; setState(null);
    try {
      await api.post("/budget/rebalance", { from_category: s.donor.key, to_category: s.category, amount: s.shortfall });
      toast.success(t("budget.rebalance_done"));
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    await s.onProceed();
  };

  let node = null;
  if (state) {
    const p = state.preview;
    const catLabel = t(CAT_LABEL[state.category] || "budget.others");
    const both = p.category.exceeds && p.total.exceeds;
    const title = both ? t("balert.title_both") : (p.category.exceeds ? t("balert.title") : t("balert.title_total"));
    node = (
      <Modal open onClose={back} title={title} testid="budget-alert-modal" centerTitle>
        <div className="space-y-4 text-sm">
          {both ? (
            <>
              <p className="text-xmas-slate">{t("balert.intro_both")}</p>
              <ul className="space-y-1.5">
                <li className="flex items-center gap-2 text-xmas-champagne" data-testid="balert-cat-line"><AlertTriangle className="h-4 w-4 shrink-0 text-xmas-gold" /> {t("balert.cat_over", { cat: catLabel, v: fmt(p.category.over_after) })}</li>
                <li className="flex items-center gap-2 text-xmas-champagne" data-testid="balert-total-line"><AlertTriangle className="h-4 w-4 shrink-0 text-xmas-gold" /> {t("balert.total_over", { v: fmt(p.total.over_after) })}</li>
              </ul>
            </>
          ) : p.category.exceeds ? (
            <p className="text-xmas-slate">{t("balert.intro", { cat: catLabel })}</p>
          ) : (
            <p className="text-xmas-slate">{t("balert.intro_total", { cat: catLabel })}</p>
          )}

          <div className="rounded-xl border border-xmas-gold/20 bg-white/5 p-4">
            {p.category.exceeds && (
              <Row label={t("balert.your_budget")} value={fmt(p.category.limit)} />
            )}
            <Row label={t("balert.already")} value={fmt(p.category.exceeds ? p.category.planned_before : p.total.planned_before)} />
            <Row label={t("balert.this")} value={fmt(p.amount)} />
            <Row label={t("balert.new_total")} value={fmt(p.category.exceeds ? p.category.planned_after : p.total.planned_after)} highlight testid="balert-new-total" />
          </div>

          <p className="text-xmas-crimson" data-testid="balert-over-by">
            {both
              ? t("balert.over_by", { v: fmt(Math.max(p.category.over_after, p.total.over_after)) })
              : p.category.exceeds
                ? t("balert.over_by", { v: fmt(p.category.over_after) })
                : t("balert.total_over", { v: fmt(p.total.over_after) })}
          </p>

          {state.donor && (
            <div className="rounded-xl border border-xmas-pine/40 bg-xmas-pine/10 p-4" data-testid="balert-rebalance-suggest">
              <p className="flex items-start gap-2 text-xmas-snow">
                <Scale className="mt-0.5 h-4 w-4 shrink-0 text-xmas-gold" />
                {t("budget.rebalance_suggest", { v: fmt(state.donor.available), from: t(CAT_LABEL[state.donor.key]), amount: fmt(state.shortfall), to: catLabel })}
              </p>
              <div className="mt-3 flex gap-2">
                <button onClick={() => setState({ ...state, donor: null })} data-testid="balert-rebalance-no" className="flex-1 rounded-xl border border-white/15 py-2.5 text-sm font-semibold text-xmas-slate transition hover:text-xmas-snow">{t("budget.no")}</button>
                <button onClick={rebalanceAndContinue} data-testid="balert-rebalance-yes" className="flex-1 rounded-xl bg-xmas-pine py-2.5 text-sm font-bold text-white transition hover:opacity-90">{t("budget.rebalance_confirm")}</button>
              </div>
            </div>
          )}

          <p className="font-serif-elegant text-base italic text-xmas-champagne">{t("balert.question")}</p>

          <div className="flex flex-col gap-2 sm:flex-row">
            <button onClick={back} data-testid="balert-back" className="glass flex-1 rounded-xl py-3 text-sm font-semibold text-xmas-snow transition hover:border-xmas-gold/60">{t("balert.back")}</button>
            <button onClick={adjust} data-testid="balert-adjust" className="flex-1 rounded-xl border border-xmas-gold/40 py-3 text-sm font-semibold text-xmas-gold transition hover:bg-xmas-gold/10">{t("balert.adjust")}</button>
            <button onClick={cont} data-testid="balert-continue" className="btn-gold flex-1 rounded-xl py-3 text-sm font-bold">{t("balert.continue")}</button>
          </div>
        </div>
      </Modal>
    );
  }
  return { guard, node };
}

function Row({ label, value, highlight, testid }) {
  return (
    <div className="flex items-center justify-between py-1">
      <span className="text-xmas-slate">{label}</span>
      <span className={`font-mono-num ${highlight ? "text-lg text-xmas-gold" : "text-xmas-snow"}`} data-testid={testid}>{value}</span>
    </div>
  );
}
